import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import ReactPlayer from "react-player";
import CancelPresentationIcon from "@mui/icons-material/CancelPresentation";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import { call } from "services/axiosService/core";
import * as Sentry from "@sentry/react";

const ContentPlayerDialog = ({
  playContent,
  closeContentPlayer,
  content,
  userMetricsData,
  setUserMetricData,
  setViewedContent,
  userId,
  gamePlan,
  setGamePlan,
  previousGamePlan,
  setPreviousGamePlan,
  previousYearsGamePlan,
  setPreviousYearsGamePlan,
  setHasCompletedAll,
  week,
  planType,
  yearIndex,
  t,
}) => {
  const [showCongrats, setShowCongrats] = useState(false);
  const { content_url: src, content_type, open_player, title } = content;

  useEffect(() => {
    if (open_player) {
      updateMetrics();
    }
  }, []);

  const updateMetrics = () => {
    // --- Game Plan Logic ---
    if (planType && (gamePlan || previousGamePlan)) {
      let update = true;
      if (planType === "myGamePlan" && gamePlan) {
        const watched = gamePlan[0].watched_content;
        if (!watched.includes(title)) watched.push(title);
        else update = false;
      } else if (planType === "previousGamePlan" && previousGamePlan) {
        const watched = previousGamePlan[week].watched_content;
        if (!watched.includes(title)) watched.push(title);
        else update = false;
      } else if (planType === "previousYearsGamePlan" && previousYearsGamePlan) {
        const watched = previousYearsGamePlan[yearIndex - 1][week].watched_content;
        if (!watched.includes(title)) watched.push(title);
        else update = false;
      }

      if (update) {
        call(`users/${userId}/gameplan`, "PUT", {
          body: {
            game_plan: gamePlan,
            previous_game_plan: previousGamePlan,
            previous_years_game_plan: previousYearsGamePlan,
          },
        })
          .then((res) => {
            if (res.status === 200) {
              if (planType === "myGamePlan" && gamePlan) {
                const watchedLength = gamePlan[0].watched_content.length;
                const totalLength = gamePlan[0].content.length;
                if (watchedLength === totalLength) {
                  setShowCongrats(true);
                  setHasCompletedAll?.(true);
                }
              }
              setGamePlan?.([...gamePlan]);
              setPreviousGamePlan?.([...previousGamePlan]);
            }
          })
          .catch((err) => {
            console.log(err);
            Sentry.captureException(err);
          });
      }
    }

    let viewedContent = userMetricsData.viewedcontent || [];
    let weeklyContent = userMetricsData.weeklycontent || [];
    if (!weeklyContent.includes(title)) weeklyContent = [...weeklyContent, title];
    userMetricsData["weeklycontent"] = weeklyContent;
    if (!viewedContent.includes(title)) viewedContent = [...viewedContent, title];
    userMetricsData["viewedcontent"] = viewedContent;
    userMetricsData["eventkey"] = { event: "viewcontent", value: title };

    call(`users/${userId}/contentmetrics`, "PUT", { body: userMetricsData })
      .then((res) => {
        if (res.status === 200) {
          setUserMetricData(userMetricsData);
          setViewedContent?.([...viewedContent]);
        }
      })
      .catch((err) => {
        console.log(err);
        Sentry.captureException(err);
      });
  };

  const PlayerContent = () => (
    <>
      {open_player ? (
        <ReactPlayer
          url={src}
          playing={false}
          controls
          width="100%"
          config={{
            youtube: {
              playerVars: {
                rel: 0,
                modestbranding: 1,
                showinfo: 0,
                iv_load_policy: 3,
                fs: 0,
                playsinline: 1,
              },
            },
          }}
        />
      ) : (
        <MDBox display="flex" justifyContent="center" alignItems="center">
          <MDButton
            onClick={updateMetrics}
            component="a"
            href={src}
            target="_blank"
            rel="noreferrer"
            variant="outlined"
            size="large"
            color="info"
          >
            {t("playContent.openInNewTab")}
          </MDButton>
        </MDBox>
      )}
    </>
  );

  if (playContent && closeContentPlayer) {
    return (
      <Dialog
        open={playContent}
        onClose={() => closeContentPlayer(showCongrats)}
        fullWidth
        maxWidth="md"
      >
        <CancelPresentationIcon
          fontSize="large"
          onClick={() => closeContentPlayer(showCongrats)}
          style={{ color: "red", display: "block", marginLeft: "auto", cursor: "pointer" }}
        />
        <DialogTitle>{title}</DialogTitle>
        <DialogContent>
          <PlayerContent />
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <MDBox alignItems="center" color="white" margin={5}>
      <PlayerContent />
    </MDBox>
  );
};

ContentPlayerDialog.defaultProps = {
  playContent: false,
  closeContentPlayer: null,
  content: { content_url: "", content_type: "video", open_player: true, title: "" },
  userMetricsData: { viewedcontent: [] },
  setUserMetricData: () => {},
  setViewedContent: () => {},
  userId: "",
  gamePlan: null,
  setGamePlan: () => {},
  previousGamePlan: null,
  setPreviousGamePlan: () => {},

  previousYearsGamePlan: null,
  setPreviousYearsGamePlan: () => {},

  setHasCompletedAll: () => {},
  week: 0,
  planType: null,
  yearIndex: 0,
  t: (key) => key,
};

ContentPlayerDialog.propTypes = {
  playContent: PropTypes.bool,
  closeContentPlayer: PropTypes.func,
  content: PropTypes.shape({
    content_url: PropTypes.string,
    content_type: PropTypes.string,
    open_player: PropTypes.bool,
    title: PropTypes.string,
  }),
  userMetricsData: PropTypes.shape({
    viewedcontent: PropTypes.arrayOf(PropTypes.string),
  }),
  setUserMetricData: PropTypes.func,
  setViewedContent: PropTypes.func,
  userId: PropTypes.string,
  gamePlan: PropTypes.array,
  setGamePlan: PropTypes.func,
  previousGamePlan: PropTypes.array,
  setPreviousGamePlan: PropTypes.func,
  previousYearsGamePlan: PropTypes.array,
  setPreviousYearsGamePlan: PropTypes.func,
  setHasCompletedAll: PropTypes.func,
  week: PropTypes.number,
  planType: PropTypes.string,
  yearIndex: PropTypes.number,
  t: PropTypes.func,
};

export default ContentPlayerDialog;
